import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthService from "lib/Auth";
import api from "api/api";

// mui icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

//Props Validations
import PropTypes from "prop-types";

//import component
import Header from "../components/common/Header";
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import CrossStudyComparisonTable from "../components/matchNet/CrossStudyComparisonTable";
import Loader from "../components/common/Loader";

// utils
import { consumerFilterPayload } from "utils/consumerFilterUtils";

//redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadFiltersState, loadIsLoading } from "store/features/consumerFilterSlice";
import {
    loadConsumerFiltersState,
    loadAllFiltersCrossStudyState,
    loadProductSecondaryDataState,
    loadDefaultProductSecondaryDataState,
    loadAllDataMappingFiltersState,
    loadInterpolationToggleState,
    loadInterpolationDetailsState,
    loadWeightToggleState,
} from "store/features/matchNetSlice";

function MatchNetCrossStudyComparison({ headerTitle = "" }) {
    const dispatch = useDispatch();
    const [dataMappingData, setDataMappingData] = useState([]);
    const [variableMapping, setVariableMapping] = useState([]);
    const [removedKpi, setRemovedKpi] = useState([]);

    // loader
    const [isDataMappingDataLoading, setIsDataMappingDataLoading] = useState(false);
    const [isProductSecondaryDataLoading, setIsProductSecondaryDataLoading] = useState(false);

    const [cancelTokenDynamicFilter, setCancelTokenDynamicFilter] = useState(null);

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const lookupSelectedProducts = useSelector((state) => state.matchNetStore.lookupSelectedProducts);
    const productSecondaryData = useSelector((state) => state.matchNetStore.productSecondaryData);
    const selectedStudyType = useSelector((state) => state.matchNetStore.selectedStudyType);
    const filterData = useSelector((state) => state.matchNetStore.consumerFilters);
    // Current weight-toggle state, used below to preserve user selections
    // instead of blindly resetting everything to `true` on every refetch.
    const matchNetWeightToggle = useSelector((state) => state.matchNetStore.weightToggle);

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const getRemovedBackendVariables = (data) => {
        const backendVariables = data.flatMap(({ mapping }) => {
            const defaultMacroCategories = new Set((mapping?.default_mapping?.columns ?? []).map(({ macroCategory }) => macroCategory));

            return (mapping?.all_mapping?.columns ?? []).filter(({ macroCategory }) => !defaultMacroCategories.has(macroCategory)).flatMap(({ displayLabels }) => displayLabels.map(({ backendVariable }) => backendVariable));
        });

        return backendVariables;
    };

    async function fetchVariablesMappingData() {
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/deodrants-match-net-analysis/filter-data/`;
            const pros = lookupSelectedProducts.map((item) => {
                return { core_data_model_id: item.core_data_model, product_code: item.title };
            });
            const coreDataIds = [...new Set(pros.map((pro) => pro.core_data_model_id))].join(",");
            const payload = {
                core_data_model: coreDataIds,
                product_category: productCategoryId,
                products: pros,
            };
            setIsDataMappingDataLoading(true);
            await api
                .post(url, payload)
                .then((res) => {
                    const resData = res.data;
                    // const order = selectedStudyType === "home_use_test" ? ["SP&E HUT"] : ["ICT", "CLT", "Express CLT"];
                    const priorityMethods = {
                        "Home Use Test": ["SP&E HUT"],
                        "Fragrance Testing": ["ICT", "CLT", "Express CLT"],
                    };
                    const order = [
                        ...["Home Use Test", "Fragrance Testing"].flatMap((studyType) => {
                            const methods = [...new Set(resData.filter((item) => item.study_type_title === studyType).map((item) => item.study_type_method_title))];
                            const priority = priorityMethods[studyType];
                            return [...priority.filter((method) => methods.includes(method)), ...methods.filter((method) => !priority.includes(method)).sort((a, b) => a.localeCompare(b))];
                        }),
                    ];
                    const response = resData.sort((a, b) => {
                        const aIndex = order.indexOf(a.study_type_method_title);
                        const bIndex = order.indexOf(b.study_type_method_title);

                        return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
                    });
                    const updatedResponse = response.map((item) => ({
                        ...item,
                        mapping: JSON.parse(JSON.stringify(item.mapping.default_mapping.columns)),
                    }));
                    const allFilters = response.map((item) => item.mapping.all_mapping.columns);
                    const removedVariables = getRemovedBackendVariables(response);
                    setRemovedKpi(removedVariables);
                    setDataMappingData(response);
                    setVariableMapping(updatedResponse);
                    const filters = response.reduce((acc, item) => {
                        acc[item.study_type_method_title] = {
                            currentSelectedFilters: {},
                            currentSelectedAllFilters: {},
                            missingFilters: {},
                            filterSelectionStatus: false,
                            isFilterAcrossProducts: false,
                        };

                        return acc;
                    }, {});

                    dispatch(loadConsumerFiltersState(filters));
                    dispatch(loadAllFiltersCrossStudyState(allFilters));
                    dispatch(loadAllDataMappingFiltersState(response));
                    setIsDataMappingDataLoading(false);
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsDataMappingDataLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsDataMappingDataLoading(false);
        }
    }

    const getCrossStudyComparisonData = async ({ type, consumerFilter, studyTypeMethod } = {}) => {
        setIsProductSecondaryDataLoading(true);
        const requests = lookupSelectedProducts.map(async (item) => {
            const product = item.title
                .split(" ")
                .filter((ele) => ele !== "")
                .join("_");
            const url = process.env.REACT_APP_SERVER_PATH + `/deodrants-match-net-analysis/grouped/`;
            let payload = {
                core_data_id: item.core_data_model,
                product_category: productCategoryId,
                product_code: item.title,
            };
            const productStudyTypeMethod = item.study_type_method;
            if (type === "consumerFilter" || (type === "consumerFilterAll" && consumerFilter)) {
                let filters = { ...consumerFilter };
                if (productStudyTypeMethod !== studyTypeMethod) {
                    const filterStoreData = filterData[productStudyTypeMethod];
                    const pro = `${product}__${item.core_data_model}`;
                    const filtersData = filterStoreData?.currentSelectedFilters[pro] || {};
                    filters[pro] = filtersData;
                }
                let filter = await consumerFilterPayload(filters, product, item.core_data_model);
                payload["consumer_filter"] = filter;
                return api.post(url, payload);
            } else {
                return api.post(url, payload);
            }
        });

        try {
            await Promise.all(requests)
                .then((res) => {
                    const combinedResults = res.flatMap((item) =>
                        item.data.map((datum) => ({
                            ...datum,
                            weighted: lookupSelectedProducts.find((product) => `${product.title.split(" ").join("_")}__${product.core_data_model}` === `${datum.title.split(" ").join("_")}__${datum.coreDataId}`)?.weighted,
                            weightToggle: false,
                        }))
                    );
                    dispatch(loadProductSecondaryDataState(combinedResults));
                    if (!type) {
                        dispatch(loadDefaultProductSecondaryDataState(combinedResults));
                    }
                    setIsProductSecondaryDataLoading(false);
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsProductSecondaryDataLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsProductSecondaryDataLoading(false);
        }
    };

    const fetchConsumerFilter = useCallback(() => {
        if (cancelTokenDynamicFilter) cancelTokenDynamicFilter.cancel();

        if (!lookupSelectedProducts.length) return;

        const source = api.CancelToken.source();
        setCancelTokenDynamicFilter(source);

        const baseUrl =
            `${process.env.REACT_APP_SERVER_PATH}/data-mappings/dynamic-consumer-filters/?archive=false&uniq=true&combined=false&variable_category=${encodeURIComponent("Consumer Filter,Study Filter,Consumer Filter related to Product Trial")}` +
            (productCategoryId ? `&product_category=${productCategoryId}` : "");

        const requests = lookupSelectedProducts.map((product) =>
            api.get(`${baseUrl}&study_type=${product.study_type_id}&study_type_method=${product.study_type_method_id}&core_data_model=${product.core_data_model}&product_code=${product.title}`, {
                cancelToken: source.token,
                ...AuthService.getAuthHeader(),
            })
        );

        dispatch(loadIsLoading(true));
        api.all(requests)
            .then((responses) => {
                const data = responses.flatMap((r) => r.data);
                dispatch(loadFiltersState(data));
            })
            .catch((err) => {
                if (!api.isCancel(err)) console.error("Filter error", err);
            })
            .finally(() => dispatch(loadIsLoading(false)));
    }, [dispatch, lookupSelectedProducts, productCategoryId]);

    useEffect(() => {
        fetchConsumerFilter();
    }, [fetchConsumerFilter]);

    useEffect(() => {
        dispatch(loadInterpolationToggleState(false));
        dispatch(loadInterpolationDetailsState({}));
        fetchVariablesMappingData();
        getCrossStudyComparisonData();
    }, [productCategoryId, lookupSelectedProducts, handleError]);

    useEffect(() => {
        if (!dataMappingData?.length) return;
        const weightData = dataMappingData.map((item) => {
            const existing = matchNetWeightToggle.find((w) => w.studyTypeMethodTitle === item.study_type_method_title);
            return {
                studyTypeMethodTitle: item.study_type_method_title,
                weightToggle: existing ? existing.weightToggle : true,
            };
        });

        dispatch(loadWeightToggleState(weightData));
    }, [dataMappingData]);

    return (
        <>
            {(isProductSecondaryDataLoading || isDataMappingDataLoading) && <Loader />}
            <Header
                title={headerTitle}
                titleSuffix={selectedStudyType !== "" ? selectedStudyType === "home_use_test" ? "(Home Use Test Compatibility Context)" : "(Fragrance Testing Compatibility Context)" : ""}
                isStudySelected={false}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>

            <div
                style={{
                    minHeight: "calc(100vh - 170px)",
                }}
                className="full-container m-4 background-color-white  box-shadow--full-container"
            >
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 10,
                    }}
                >
                    <Link to="/match-net/lookup">
                        <div
                            className="p-2 liner-light-blue-background color-white"
                            style={{
                                borderRadius: "50%",
                                position: "relative",
                                top: -15,
                                left: -15,
                                cursor: "pointer",
                            }}
                        >
                            <span>
                                <ArrowBackIcon className="color-white" />
                            </span>
                        </div>
                    </Link>
                </div>
                {Object.keys(dataMappingData).length !== 0 && (
                    <CrossStudyComparisonTable
                        dataMappingData={dataMappingData}
                        productSecondaryData={productSecondaryData}
                        variableMapping={variableMapping}
                        setVariableMapping={setVariableMapping}
                        removedKpi={removedKpi}
                        setRemovedKpi={setRemovedKpi}
                        getCrossStudyComparisonData={getCrossStudyComparisonData}
                    />
                )}
            </div>
        </>
    );
}

// Typechecking props for the MatchNetCrossStudyComparison
MatchNetCrossStudyComparison.propTypes = {
    headerTitle: PropTypes.string,
};

export default MatchNetCrossStudyComparison;
